import 'leaflet/dist/leaflet.css';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend as ChartLegend,
  LinearScale,
  Title,
  Tooltip
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { GeoJSON, MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import MapLegend from './MapLegend';
import {
  countyNameToCaCountyPrefix,
  normalizeTractGeofips,
  walkabilityClassFromPercentile,
  WALKABILITY_CLASS_ORDER
} from '../../utils/walkabilityUtils';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Title, Tooltip, ChartLegend);

/** Low walkability → red; high → green (matches decile bins left→right in legend). */
const WALK_PALETTE = ['#7f0000', '#b2182b', '#d73027', '#f46d43', '#fdae61', '#fee08b', '#d9ef8b', '#a6d96a', '#66bd63', '#1a9850'];

function buildHistBreaks(values: number[]): number[] {
  const sorted = [...values].filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (sorted.length === 0) return [];
  const minV = sorted[0];
  const maxV = sorted[sorted.length - 1];
  const dec: number[] = [];
  for (let i = 1; i <= 9; i++) {
    const idx = Math.floor((sorted.length * i) / 10);
    dec.push(sorted[Math.min(idx, sorted.length - 1)]);
  }
  return [minV, ...dec, maxV];
}

function colorForWalkIndex(v: number, breaks: number[]): string {
  if (breaks.length < 2) return '#ccc';
  for (let i = 0; i < WALK_PALETTE.length; i++) {
    const upper = breaks[Math.min(i + 1, breaks.length - 1)];
    if (v <= upper || i === WALK_PALETTE.length - 1) return WALK_PALETTE[i];
  }
  return WALK_PALETTE[WALK_PALETTE.length - 1];
}

function FitBounds({ geojson }: { geojson: object }) {
  const map = useMap();
  useEffect(() => {
    try {
      const layer = L.geoJSON(geojson as any);
      const b = layer.getBounds();
      if (b.isValid()) map.fitBounds(b, { padding: [24, 24], maxZoom: 12 });
    } catch {
      /* ignore */
    }
  }, [geojson, map]);
  return null;
}

export interface WalkabilityTractSectionProps {
  location: string;
  geofips: number | null;
}

type WalkRow = {
  geofips: string;
  nat_walk_ind: number | null;
  nat_walk_ind_percentile: number | null;
  tot_pop: number | null;
};

async function resolveCountyPrefix(
  locationName: string,
  geofips: number | null
): Promise<{ prefix: string; displayCounty: string } | null> {
  const trimmed = locationName.trim();
  const lower = trimmed.toLowerCase();
  if (!trimmed || lower === 'california' || lower.includes('state')) return null;

  if (lower.endsWith('county')) {
    const prefixFromName = countyNameToCaCountyPrefix(trimmed);
    if (prefixFromName) {
      return { prefix: prefixFromName, displayCounty: trimmed.replace(/\s+county$/i, '').trim() + ' County' };
    }
    if (geofips != null) {
      const g = String(Math.trunc(geofips)).padStart(5, '0');
      if (g.startsWith('06') && g.length === 5) {
        return { prefix: g, displayCounty: trimmed };
      }
    }
    return null;
  }

  await supabaseLocationService.initialize();
  const county = supabaseLocationService.getCountyForCity(trimmed);
  if (!county) {
    const { data } = await supabaseClient
      .from('regions')
      .select('county')
      .ilike('location_name', trimmed)
      .eq('geography_type', 'City')
      .limit(1)
      .maybeSingle();
    const c = data?.county;
    if (!c) return null;
    const prefix = countyNameToCaCountyPrefix(c);
    if (!prefix) return null;
    return { prefix, displayCounty: c };
  }
  const prefix = countyNameToCaCountyPrefix(county);
  if (!prefix) return null;
  return { prefix, displayCounty: county };
}

export const WalkabilityTractSection: React.FC<WalkabilityTractSectionProps> = ({ location, geofips }) => {
  const [geoJsonAll, setGeoJsonAll] = useState<any>(null);
  const [rows, setRows] = useState<WalkRow[]>([]);
  const [countyMeta, setCountyMeta] = useState<{ prefix: string; displayCounty: string } | null>(null);
  const [placeTractIds, setPlaceTractIds] = useState<Set<string> | null>(null);
  const [isCountyLocation, setIsCountyLocation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/data/california_tracts_optimized.geojson');
        const data = await res.json();
        if (!cancelled) setGeoJsonAll(data);
      } catch {
        if (!cancelled) setError('Failed to load tract boundaries.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRows([]);
    setCountyMeta(null);
    setPlaceTractIds(null);
    try {
      const countyLoc = location.trim().toLowerCase().endsWith('county');
      setIsCountyLocation(countyLoc);

      const resolved = await resolveCountyPrefix(location, geofips);
      if (!resolved) {
        setError('Walkability maps need a California city or county (not statewide).');
        setLoading(false);
        return;
      }
      setCountyMeta(resolved);

      const { data: wRows, error: wErr } = await supabaseClient
        .from('walkability_tract')
        .select('geofips,nat_walk_ind,nat_walk_ind_percentile,tot_pop')
        .like('geofips', `${resolved.prefix}%`);

      if (wErr) throw wErr;
      const list = (wRows || []) as WalkRow[];
      setRows(list);

      if (!countyLoc && geofips != null) {
        const stplc = String(Math.trunc(geofips)).padStart(7, '0');
        const { data: rel, error: rErr } = await supabaseClient
          .from('relationship_trct_place_20')
          .select('trct')
          .eq('stplc', stplc);
        if (rErr) {
          console.warn('WalkabilityTractSection: crosswalk', rErr.message);
          setPlaceTractIds(new Set());
        } else {
          const ids = new Set<string>();
          (rel || []).forEach((r: { trct?: string }) => {
            const id = normalizeTractGeofips(r.trct);
            if (id) ids.add(id);
          });
          setPlaceTractIds(ids);
        }
      } else {
        setPlaceTractIds(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load walkability data.');
    } finally {
      setLoading(false);
    }
  }, [location, geofips]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const byGeofips = useMemo(() => {
    const m = new Map<string, WalkRow>();
    rows.forEach((r) => {
      const k = normalizeTractGeofips(r.geofips);
      if (k) m.set(k, r);
    });
    return m;
  }, [rows]);

  const countyGeoJson = useMemo(() => {
    if (!geoJsonAll?.features || !countyMeta) return null;
    const prefix = countyMeta.prefix;
    const feats = geoJsonAll.features.filter((f: any) => {
      const g = normalizeTractGeofips(f.properties?.GEOID);
      return g.startsWith(prefix);
    });
    return { type: 'FeatureCollection' as const, features: feats };
  }, [geoJsonAll, countyMeta]);

  const natWalkValues = useMemo(
    () => rows.map((r) => r.nat_walk_ind).filter((v): v is number => v != null && Number.isFinite(Number(v))),
    [rows]
  );

  const histBreaks = useMemo(() => buildHistBreaks(natWalkValues), [natWalkValues]);

  const getStyle = useCallback(
    (feature: any) => {
      const gid = normalizeTractGeofips(feature?.properties?.GEOID);
      const row = byGeofips.get(gid);
      const v = row?.nat_walk_ind;
      if (v == null || !Number.isFinite(Number(v)) || histBreaks.length < 2) {
        return {
          fillColor: '#e0e0e0',
          weight: 0.4,
          opacity: 1,
          color: '#fff',
          fillOpacity: 0.35
        };
      }
      const nv = Number(v);
      return {
        fillColor: colorForWalkIndex(nv, histBreaks),
        weight: 0.5,
        opacity: 1,
        color: '#fff',
        fillOpacity: 0.82
      };
    },
    [byGeofips, histBreaks]
  );

  const onEachFeature = useCallback(
    (feature: any, layer: L.Layer) => {
      const gid = normalizeTractGeofips(feature?.properties?.GEOID);
      const row = byGeofips.get(gid);
      const pct = row?.nat_walk_ind_percentile;
      const cls = walkabilityClassFromPercentile(pct);
      const lines = [
        `<strong>Tract ${gid}</strong>`,
        row?.nat_walk_ind != null
          ? `Walkability index: <strong>${Number(row.nat_walk_ind).toFixed(2)}</strong>`
          : 'No index value',
        pct != null ? `CA percentile: ${Number(pct).toFixed(1)}` : '',
        cls ? `Class: ${cls}` : ''
      ].filter(Boolean);
      layer.bindPopup(lines.join('<br/>'));
    },
    [byGeofips]
  );

  const barCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    WALKABILITY_CLASS_ORDER.forEach((c) => {
      counts[c] = 0;
    });

    const consider = (tractId: string) => {
      const row = byGeofips.get(tractId);
      if (!row) return;
      const c = walkabilityClassFromPercentile(row.nat_walk_ind_percentile);
      if (c && counts[c] !== undefined) counts[c] += 1;
    };

    if (isCountyLocation) {
      byGeofips.forEach((_row, tractId) => consider(tractId));
    } else if (placeTractIds != null) {
      placeTractIds.forEach((id) => consider(id));
    } else if (!isCountyLocation && geofips != null) {
      return null;
    }

    return counts;
  }, [byGeofips, placeTractIds, isCountyLocation, geofips]);

  const barChartData = useMemo(() => {
    if (!barCounts) {
      return {
        labels: [...WALKABILITY_CLASS_ORDER].reverse(),
        datasets: [
          {
            label: 'Tracts',
            data: [0, 0, 0, 0, 0],
            backgroundColor: ['#1a9850', '#66bd63', '#fee08b', '#fdae61', '#d73027']
          }
        ]
      };
    }
    const labels = [...WALKABILITY_CLASS_ORDER].reverse();
    return {
      labels,
      datasets: [
        {
          label: 'Number of tracts',
          data: labels.map((l) => barCounts[l] ?? 0),
          backgroundColor: ['#1a9850', '#66bd63', '#fee08b', '#fdae61', '#d73027']
        }
      ]
    };
  }, [barCounts]);

  const legendConfig = useMemo(
    () => ({
      title: 'National Walkability Index',
      dataYear: 'EPA NWI (CBG→tract), CA',
      colorScale: [...WALK_PALETTE],
      breaks: histBreaks,
      unit: '',
      tractCount: rows.length,
      referenceValue: undefined as { label: string; value: number } | undefined
    }),
    [histBreaks, rows.length]
  );

  if (loading && !countyGeoJson) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        Loading walkability data…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '1rem', color: '#b91c1c', background: '#fef2f2', borderRadius: 8 }}>
        {error}
      </div>
    );
  }

  if (!countyGeoJson || !countyMeta) {
    return null;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <p style={{ margin: '0 0 12px', color: '#444', fontSize: 14, lineHeight: 1.5 }}>
          Census tracts in <strong>{countyMeta.displayCounty}</strong> colored by EPA National Walkability Index
          (higher scores = more walkable). Place summary counts (second chart) use tracts that intersect{' '}
          <strong>{location}</strong>
          {isCountyLocation ? ' (all county tracts).' : '.'}
        </p>
        <div style={{ position: 'relative', height: 480, borderRadius: 8, overflow: 'hidden' }}>
          <MapContainer
            center={[36.7783, -119.4179]}
            zoom={8}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={false}
            whenReady={() => setTimeout(() => setMapReady(true), 200)}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap'
            />
            {mapReady && countyGeoJson && histBreaks.length >= 2 && (
              <>
                <FitBounds geojson={countyGeoJson} />
                <GeoJSON data={countyGeoJson} style={getStyle} onEachFeature={onEachFeature} />
              </>
            )}
          </MapContainer>
          {rows.length > 0 && histBreaks.length >= 2 && (
            <div className="map-legend-wrapper">
              <MapLegend
                title={legendConfig.title}
                dataYear={legendConfig.dataYear}
                colorScale={legendConfig.colorScale}
                breaks={legendConfig.breaks}
                unit={legendConfig.unit}
                tractCount={legendConfig.tractCount}
              />
            </div>
          )}
        </div>
      </div>

      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: 16 }}>
          Walkability classification — tracts in {location}
        </h4>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: '#555' }}>
          Classes use California tract percentiles of the walkability index (approx. quintiles): highly walkable
          (top 20%) through least walkable (bottom 20%).
        </p>
        {loading ? (
          <p style={{ color: '#666' }}>Loading classification…</p>
        ) : !isCountyLocation && placeTractIds != null && placeTractIds.size === 0 ? (
          <p style={{ color: '#666' }}>No tract–place crosswalk rows found for this location.</p>
        ) : barCounts == null ? (
          <p style={{ color: '#666' }}>Unable to load place tracts for classification.</p>
        ) : (
          <div style={{ maxWidth: 720, height: 320 }}>
            <Bar
              data={barChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: 'y',
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => `${ctx.parsed.x?.toLocaleString?.() ?? ctx.parsed.x} tracts`
                    }
                  }
                },
                scales: {
                  x: {
                    title: { display: true, text: 'Number of census tracts' },
                    ticks: { precision: 0 }
                  }
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default WalkabilityTractSection;
